/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * All possible values for the `type` field of a question.
 */
type QuestionType = "text" | "blank" | "choice";

/**
 * All possible values for the `appearance` field of a question.
 */
type QuestionAppearance =
	| "multiline"
	| "text"
	| "sub-text"
	| "singleline"
	| "stars"
	| "thumbs"
	| "smiles"
	| "radio"
	| "checkbox";

/**
 * A possible choice for a multiple choice question.
 *
 * @typeparam T - The type of the value of the choice
 * @typeparam L - The type of the label of the choice, defaulting to `string`
 */
interface Choice<T, L = string> {
	label: L;
	value: T;
}

/**
 * Possible values for a star question choice
 */
type StarChoiceValue = 2 | 4 | 6 | 8 | 10;

/**
 * Possible choices for a star question, with exact labels and values specified.
 */
export type StarChoices = [
	Choice<2, "1 / 5">,
	Choice<4, "2 / 5">,
	Choice<6, "3 / 5">,
	Choice<8, "4 / 5">,
	Choice<10, "5 / 5">,
];

/**
 * All possible choices for a thumbs question.
 */
export type ThumbChoices = [Choice<0, "Not Great">, Choice<10, "Great!">];

/**
 * All possible choices for a smiles question.
 */
export type SmileChoices = [
	Choice<1, "Very Unsatisfied">,
	Choice<2, "Unsatisfied">,
	Choice<3, "Neutral">,
	Choice<4, "Satisfied">,
	Choice<5, "Very Satisfied">,
];

/**
 * The choices for a radio / checkbox button question. The set of choices for a radio / checkbox
 * question is unspecified, since the labels and values are determined by user
 * input.
 */
export type RadioOrCheckboxChoices = Choice<string>[];

/**
 * A union type of all choices possible for a valid multiple choice question.
 */
export type Choices = StarChoices | ThumbChoices | SmileChoices | RadioOrCheckboxChoices;

/**
 * An interface defining the general type of a question. This type is much more
 * simple than that of {@see Question}, and is used as a fallback. The types of
 * `type` and `appearance` are not restricted to the actual values possible.
 */
interface BaseQuestion {
	id: string;
	title: string;
	type: QuestionType;
	appearance: QuestionAppearance;
}

interface BaseChoicesQuestion extends BaseQuestion {
	type: "choice";
	appearance: Extract<QuestionAppearance, "stars" | "thumbs" | "smiles" | "radio" | "checkbox">;
	choices: readonly Choice<string | number>[];
}

/**
 * The unique fields of a star question. The fixed value for appearance means
 * this can be used in a discriminated union.
 */
interface StarsQuestion extends BaseChoicesQuestion {
	appearance: "stars";
	choices: StarChoices;
	value?: StarChoiceValue;
}

/**
 * An interface defining the choices that are available for a question with the
 * appearance "thumbs."
 */
interface ThumbsQuestion extends BaseChoicesQuestion {
	appearance: "thumbs";
	choices: ThumbChoices;
}

/**
 * An interface defining the choices that are available for a question with the
 * appearance "smiles."
 */
interface SmilesQuestion extends BaseChoicesQuestion {
	appearance: "smiles";
	choices: SmileChoices;
}

/**
 * An interface defining the choices that are available for a question with the
 * appearance "radio". Recall that {@see RadioOrCheckboxChoices} is not a fixed set of
 * values, but rather a type describing an array of choices.
 */
interface RadioQuestion extends BaseChoicesQuestion {
	appearance: "radio";
	choices: RadioOrCheckboxChoices;
}

/**
 * An interface defining the choices that are available for a question with the
 * appearance "checkbox". Recall that {@see RadioOrCheckboxChoices} is not a fixed set of
 * values, but rather a type describing an array of choices.
 */
interface CheckboxQuestion extends BaseChoicesQuestion {
	appearance: "checkbox";
	choices: RadioOrCheckboxChoices;
}

/**
 * A type that representing a question with the `type` field set to "choice."
 * This is the type of a value after checking if the `field` is equal to
 * "choice." To get a more specific type from here, check what the value of the
 * `appearance` field is.
 */
export type ChoicesQuestion = StarsQuestion | ThumbsQuestion | SmilesQuestion | RadioQuestion | CheckboxQuestion;

/**
 * The type of a question with a `type` of "text." An appearance of "multiline"
 * means long answer, while "singleline" means short answer.
 */
interface TextQuestion extends BaseQuestion {
	type: "text";
	appearance: Extract<QuestionAppearance, "multiline" | "singleline">;
	give_feedback_label?: string; // eslint-disable-line camelcase
}

/**
 * A blank question is used for headings. There is no value, and the only
 * possible appearance at this point is "text."
 */
interface BlankQuestion extends BaseQuestion {
	type: "blank";
	appearance: "text" | "sub-text";
}

/**
 * This is the type of all questions. It uses unions to allow for refining of
 * the type with type guards on a discriminator (like `type` or `appearance`),
 * and intersections with more general types for fields that are common to all
 * variants.
 */
export type Question = TextQuestion | ChoicesQuestion | BlankQuestion;

/**
 * This exception is thrown from all validation functions, and is caught in
 * {@see wrapValidationFunction}. If it is thrown, it signifies that the data
 * does not conform to the types described above.
 */
class ValidationError extends Error {}

/**
 * This function wraps a validation function in a callback that returns
 * `arg is T`. This means that it can be used as a type guard. Once a value has
 * been checked by this function, typescript treats it as `T`.
 *
 * @typeparam T - The type that the validation function guards.
 *
 * @param fn - A function that takes any value and returns true if the value is
 * of type T and false if not. Alternatively, a {@see ValidationError} can be
 * thrown with a descriptive message of the problem with the value.
 * @returns A function that wraps the passed in validation function.
 */
function wrapValidationFunction<T>(fn: (a: any) => a is T): (a: any) => a is T {
	return (a: any): a is T => {
		try {
			return fn(a);
		} catch (e) {
			if (e instanceof ValidationError) {
				return false;
			}
			throw e;
		}
	};
}

const requiredFields: Partial<{ [k in keyof Question]: string }> = {
	id: "string",
	title: "string",
	type: "string",
	appearance: "string",
};

type HasRequiredFields = { [k in keyof typeof requiredFields]: string };
function validateBlankQuestion(question: HasRequiredFields) {
	if (question.appearance === "text" || question.appearance === "sub-text") {
		return true;
	}
	throw new ValidationError(`Expected blank question to have appearance "text", found "${question.appearance}"`);
}

function validateTextQuestion(question: HasRequiredFields) {
	if (question.appearance === "singleline" || question.appearance === "multiline") {
		return true;
	}
	throw new ValidationError(
		`Expected text question to have appearance of "singleline" or "multiline", found "${question.appearance}"`,
	);
}

type HasChoices = HasRequiredFields & { choices: { label: string; value: any }[] };

function validateStarsQuestion(question: HasChoices & { value?: any }) {
	if (question.choices.length !== 5) {
		throw new ValidationError(`Expected star question to have 5 choices, found ${question.choices.length}`);
	}

	if (question.choices.every((choice, i) => choice.label === `${i + 1} / 5` && choice.value === (i + 1) * 2)) {
		return true;
	}
	throw new ValidationError(`Expected star question choices to have label of \`\${choice.value / 2} / 5\``);
}

function validateThumbsQuestion(question: HasChoices) {
	const [choice1, choice2] = question.choices;

	if (!choice1 || !choice2) {
		throw new ValidationError(`Expected thumbs question to have 2 choices, found ${question.choices}`);
	}

	const valid =
		choice1.label === "Not Great" && choice1.value === 0 && choice2.label === "Great!" && choice2.value === 10;
	if (valid) {
		return valid;
	}

	throw new ValidationError(
		`Expected thumbs question to have choices of ` +
			`[{ "label": "Not Great", "value": 0 }, { "label": "Great!", "value": 10 }], found ${JSON.stringify(
				question.choices,
			)}`,
	);
}

function validateSmilesQuestion(question: HasChoices) {
	const [verySad, sad, meh, happy, veryHappy] = question.choices;

	if (!verySad || !sad || !meh || !happy || !veryHappy) {
		throw new ValidationError(`Expected smiles question to have 5 choices, found ${question.choices.length}`);
	}

	const valid =
		verySad.label === "Very Unsatisfied" &&
		verySad.value === 1 &&
		sad.label === "Unsatisfied" &&
		sad.value === 2 &&
		meh.label === "Neutral" &&
		meh.value === 3 &&
		happy.label === "Satisfied" &&
		happy.value === 4 &&
		veryHappy.label === "Very Satisfied" &&
		veryHappy.value === 5;
	if (valid) {
		return valid;
	}

	throw new ValidationError(
		`Expected smiles question to have choices of ${JSON.stringify([
			{ label: "Not Great", value: 0 },
			{ label: "OK", value: 5 },
			{ label: "Great!", value: 10 },
		])}, found ${JSON.stringify(question.choices)}`,
	);
}

function validateRadioOrCheckboxQuestion(question: HasChoices) {
	if (question.choices.length < 1) {
		throw new ValidationError(
			`Expected ${question.appearance} question to have at least 1 choices, found ${question.choices.length}`,
		);
	}

	question.choices.forEach(choice => {
		if (typeof choice.value !== "string") {
			throw new ValidationError(
				`Expected ${
					question.appearance
				} question choices to have values of type "string", found "${typeof choice.value}"`,
			);
		}
	});

	return true;
}

function validateChoiceQuestion(question: HasRequiredFields & { choices?: any }) {
	if (!question.choices || !Array.isArray(question.choices)) {
		throw new ValidationError(
			`Expected choice question choices field to have type array, found ${typeof question.choices}`,
		);
	}

	question.choices.forEach((choice: any) => {
		if (
			typeof choice !== "object" ||
			typeof choice.label !== "string" ||
			!["string", "number"].includes(typeof choice.value)
		) {
			throw new ValidationError(
				`Expected choice to be { label: string; value: string | number }, found ${
					typeof choice === "object" ? JSON.stringify(choice) : choice
				}`,
			);
		}
		return true;
	});

	switch (question.appearance) {
		case "stars":
			// @ts-ignore  Type guards were not working for some reason
			return validateStarsQuestion(question);
		case "thumbs":
			// @ts-ignore
			return validateThumbsQuestion(question);
		case "smiles":
			// @ts-ignore
			return validateSmilesQuestion(question);
		case "checkbox":
		case "radio":
			// @ts-ignore
			return validateRadioOrCheckboxQuestion(question);
		default:
			return false;
	}
}

export function validateQuestion(question: any): question is Question {
	if (typeof question !== "object") {
		throw new ValidationError(`Expected question to have type "object", found "${typeof question}"`);
	}

	Object.entries(requiredFields).forEach(([field, expectedType]) => {
		const actualType = typeof question[field];
		if (actualType !== expectedType) {
			throw new ValidationError(`Expected question.${field} to have type "${expectedType}", got "${actualType}"`);
		}
	});

	// assert: question is now HasRequiredFields
	switch (question.type) {
		case "blank":
			return validateBlankQuestion(question);
		case "text":
			return validateTextQuestion(question);
		case "choice":
			return validateChoiceQuestion(question);
		default:
			return false;
	}
}

export function validateQuestions(questions: readonly any[] | undefined): questions is Question[] {
	return !!questions && questions.length > 0 && questions.every(validateQuestion);
}

export const isQuestion = wrapValidationFunction(validateQuestion);
export const isQuestionArray = wrapValidationFunction(validateQuestions);

export interface DraftEntry {
	id: string;
	value: string | { label: string; value: string | number } | { label: string; value: string }[];
}

export type Draft = DraftEntry[];

export function validateDraft(questions: readonly Question[], draft: any): true {
	if (!Array.isArray(draft)) {
		throw new ValidationError("Expected draft to be an array");
	}

	draft.forEach(response => {
		if (typeof response !== "object") {
			throw new ValidationError("Expected draft entries to be objects");
		}
		if (typeof response.id !== "string") {
			throw new ValidationError("Expected draft entries to have an 'id' property of type string");
		}

		const question = questions.find(({ id }) => id === response.id);
		if (!question) {
			throw new ValidationError("Draft entry has no corresponding question");
		}

		if (question.type === "choice") {
			if (typeof response.value !== "object") {
				throw new ValidationError("Draft entry for choice question should have a value of type object");
			}

			type Choice = (typeof question.choices)[number];

			if (Array.isArray(response.value) && question.appearance === "checkbox") {
				const valueList = response.value.map((c: Choice) => c.value);
				const choiceOptions = question.choices.map(({ value }) => value);

				// There should be at last one choice element in response.value
				if (valueList.some((v: string) => !choiceOptions.includes(v))) {
					throw new ValidationError("Draft entry for checkbox choice question has no corresponding choice");
				}
			} else {
				// Can't use `Array.prototype.find` here of big type error, maybe this:
				// https://github.com/microsoft/TypeScript/issues/33591
				let choice: Choice | undefined;
				question.choices.forEach((c: Choice) => {
					if (c.value === response.value.value) {
						choice = c;
					}
				});

				if (!choice) {
					throw new ValidationError("Draft entry for choice question has no corresponding choice");
				}
			}
		} else if (question.type === "blank") {
			throw new ValidationError("Found draft entry for heading question");
		} else if (question.type === "text" && typeof response.value !== "string") {
			throw new ValidationError("Expected draft entry for text question to have value of type string");
		}
	});

	return true;
}

/**
 * Determine if a {@see Draft} conforms to the specification defined by the
 * types above, and also that it corresponds to the set of questions it responds
 * to.
 */
export function isValidDraft(questions: readonly Question[], draft: any): draft is Draft {
	try {
		return validateDraft(questions, draft);
	} catch (e) {
		if (e instanceof ValidationError) {
			return false;
		}
		throw e;
	}
}
